import type { ColumnDef } from "@tanstack/react-table";
import { SimpleTable } from "@/core/components/simple-table";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";

interface RosterSkeletonRow {
  id: string;
}

const skeletonHeader = (width: string) => () => (
  <Skeleton className={cn("h-4", width)} />
);

const SKELETON_COLUMNS: ColumnDef<RosterSkeletonRow>[] = [
  {
    id: "tech",
    header: skeletonHeader("w-20"),
    enableSorting: false,
    meta: { size: "224px" },
  },
  {
    id: "role",
    header: skeletonHeader("w-16"),
    enableSorting: false,
    meta: { size: "176px" },
  },
  {
    id: "email",
    header: skeletonHeader("w-24"),
    enableSorting: false,
    meta: { size: "260px" },
  },
  {
    id: "pay-plan",
    header: skeletonHeader("w-20"),
    enableSorting: false,
    meta: { size: "240px" },
  },
  {
    id: "department",
    header: skeletonHeader("w-24"),
    enableSorting: false,
    meta: { size: "200px" },
  },
  {
    id: "reports-to",
    header: skeletonHeader("w-20"),
    enableSorting: false,
    meta: { size: "220px" },
  },
];

export const RosterTableSkeleton = ({
  loadingRowCount = 8,
}: {
  loadingRowCount?: number;
}) => (
  <Stack
    align="start"
    gap={0}
    className="min-h-0 w-full flex-1"
    aria-busy="true"
  >
    <span role="status" aria-live="polite" className="sr-only">
      Loading roster table
    </span>
    {/* A picture of the table to come: inert, so screen readers skip its
        blank headers and cells (the status above says it is loading) and the
        keyboard skips its scroll area. */}
    <Stack inert gap={0} align="stretch" className="min-h-0 w-full flex-1">
      <SimpleTable
        data={[]}
        columns={SKELETON_COLUMNS}
        isLoading
        loadingRowCount={loadingRowCount}
        className="h-full w-full"
        scrollContentStyle={{ minWidth: "1320px" }}
        columnPinning={{ left: ["tech"] }}
        respectColumnWidths
      />
    </Stack>
  </Stack>
);
